import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { Check, ChevronDown, X, Palette } from 'lucide-react';
import useModalBack from '../../hooks/useModalBack';

const PRESET_COLORS = [
  // Row 1 - Blues
  '#3b82f6', '#2563eb', '#1d4ed8', '#1e40af', '#60a5fa', '#93c5fd',
  // Row 2 - Purples
  '#6366f1', '#4f46e5', '#4338ca', '#3730a3', '#8b5cf6', '#a78bfa',
  // Row 3 - Pinks
  '#ec4899', '#db2777', '#be185d', '#9d174d', '#f472b6', '#f9a8d4',
  // Row 4 - Reds
  '#ef4444', '#dc2626', '#b91c1c', '#991b1b', '#f87171', '#fca5a5',
  // Row 5 - Oranges
  '#f97316', '#ea580c', '#c2410c', '#9a3412', '#fb923c', '#fdba74',
  // Row 6 - Yellows
  '#eab308', '#ca8a04', '#a16207', '#854d0e', '#facc15', '#fde047',
  // Row 7 - Greens
  '#22c55e', '#16a34a', '#15803d', '#166534', '#4ade80', '#86efac',
  // Row 8 - Teals
  '#14b8a6', '#0d9488', '#0f766e', '#115e59', '#2dd4bf', '#5eead4',
  // // Row 9 - Cyans
  // '#06b6d4', '#0891b2', '#0e7490', '#155e75', '#22d3ee', '#67e8f9',
  // Row 10 - Grays
  '#64748b', '#475569', '#334155', '#1e293b', '#94a3b8', '#cbd5e1',
];

// Hue colors for the hue slider
const HUE_COLORS = [
  '#ff0000', '#ff8000', '#ffff00', '#80ff00', '#00ff00', '#00ff80',
  '#00ffff', '#0080ff', '#0000ff', '#8000ff', '#ff00ff', '#ff0080', '#ff0000'
];

// Convert HSV to RGB
function hsvToRgb(h, s, v) {
  let r, g, b;
  const i = Math.floor(h * 6);
  const f = h * 6 - i;
  const p = v * (1 - s);
  const q = v * (1 - f * s);
  const t = v * (1 - (1 - f) * s);

  switch (i % 6) {
    case 0: r = v; g = t; b = p; break;
    case 1: r = q; g = v; b = p; break;
    case 2: r = p; g = v; b = t; break;
    case 3: r = p; g = q; b = v; break;
    case 4: r = t; g = p; b = v; break;
    case 5: r = v; g = p; b = q; break;
    default: r = 0; g = 0; b = 0;
  }

  return {
    r: Math.round(r * 255),
    g: Math.round(g * 255),
    b: Math.round(b * 255)
  };
}

// Convert RGB to Hex
function rgbToHex(r, g, b) {
  return '#' + [r, g, b].map(x => x.toString(16).padStart(2, '0')).join('');
}

// Convert Hex to HSV
function hexToHsv(hex) {
  const r = parseInt(hex.slice(1, 3), 16) / 255;
  const g = parseInt(hex.slice(3, 5), 16) / 255;
  const b = parseInt(hex.slice(5, 7), 16) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const d = max - min;

  let h = 0;
  const s = max === 0 ? 0 : d / max;
  const v = max;

  if (max !== min) {
    switch (max) {
      case r: h = (g - b) / d + (g < b ? 6 : 0); break;
      case g: h = (b - r) / d + 2; break;
      case b: h = (r - g) / d + 4; break;
      default: h = 0;
    }
    h /= 6;
  }

  return { h, s, v };
}

// Custom Color Picker Modal Component
function CustomColorPickerModal({ isOpen, onClose, value, onChange }) {
  const [hsv, setHsv] = useState({ h: 0, s: 1, v: 1 });
  const [tempColor, setTempColor] = useState(value || '#6366f1');
  const colorAreaRef = useRef(null);
  const hueSliderRef = useRef(null);
  const isDraggingColor = useRef(false);
  const isDraggingHue = useRef(false);

  useModalBack(isOpen, onClose);

  useEffect(() => {
    if (isOpen && value) {
      const newHsv = hexToHsv(value);
      setHsv(newHsv);
      setTempColor(value);
    }
  }, [isOpen, value]);

  const updateColorFromHsv = useCallback((newHsv) => {
    const rgb = hsvToRgb(newHsv.h, newHsv.s, newHsv.v);
    const hex = rgbToHex(rgb.r, rgb.g, rgb.b);
    setTempColor(hex);
  }, []);

  const handleColorAreaInteraction = useCallback((e) => {
    if (!colorAreaRef.current) return;
    const rect = colorAreaRef.current.getBoundingClientRect();
    const x = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    const y = Math.max(0, Math.min(1, (e.clientY - rect.top) / rect.height));

    const newHsv = { ...hsv, s: x, v: 1 - y };
    setHsv(newHsv);
    updateColorFromHsv(newHsv);
  }, [hsv, updateColorFromHsv]);

  const handleHueInteraction = useCallback((e) => {
    if (!hueSliderRef.current) return;
    const rect = hueSliderRef.current.getBoundingClientRect();
    const x = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));

    const newHsv = { ...hsv, h: x };
    setHsv(newHsv);
    updateColorFromHsv(newHsv);
  }, [hsv, updateColorFromHsv]);

  const handleColorMouseDown = (e) => {
    isDraggingColor.current = true;
    handleColorAreaInteraction(e);
  };

  const handleHueMouseDown = (e) => {
    isDraggingHue.current = true;
    handleHueInteraction(e);
  };

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (isDraggingColor.current) {
        handleColorAreaInteraction(e);
      }
      if (isDraggingHue.current) {
        handleHueInteraction(e);
      }
    };

    const handleMouseUp = () => {
      isDraggingColor.current = false;
      isDraggingHue.current = false;
    };

    if (isOpen) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.addEventListener('touchmove', handleMouseMove);
      document.addEventListener('touchend', handleMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.removeEventListener('touchmove', handleMouseMove);
      document.removeEventListener('touchend', handleMouseUp);
    };
  }, [isOpen, handleColorAreaInteraction, handleHueInteraction]);

  const handleTouchStart = (e, type) => {
    e.preventDefault();
    const touch = e.touches[0];
    if (type === 'color') {
      isDraggingColor.current = true;
      handleColorAreaInteraction(touch);
    } else {
      isDraggingHue.current = true;
      handleHueInteraction(touch);
    }
  };

  const handleApply = () => {
    onChange(tempColor);
    onClose();
  };

  if (!isOpen) return null;

  const hueColor = rgbToHex(...Object.values(hsvToRgb(hsv.h, 1, 1)));

  return (
    <div
      className="fixed inset-0 bg-surface-900/60 backdrop-blur-sm flex items-center justify-center z-[110] p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-xs"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-surface-200">
          <h3 className="text-lg font-semibold text-surface-900">Custom Color</h3>
          <button
            type="button"
            onClick={onClose}
            className="p-2 text-surface-400 hover:text-surface-600 rounded-lg transition-colors hover:bg-surface-100"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-4 space-y-4">
          {/* Color Area */}
          <div
            ref={colorAreaRef}
            className="relative w-full h-40 rounded-xl cursor-crosshair overflow-hidden"
            style={{
              background: `linear-gradient(to top, #000, transparent), linear-gradient(to right, #fff, ${hueColor})`
            }}
            onMouseDown={handleColorMouseDown}
            onTouchStart={(e) => handleTouchStart(e, 'color')}
          >
            {/* Color Picker Indicator */}
            <div
              className="absolute w-5 h-5 border-2 border-white rounded-full shadow-lg transform -translate-x-1/2 -translate-y-1/2 pointer-events-none"
              style={{
                left: `${hsv.s * 100}%`,
                top: `${(1 - hsv.v) * 100}%`,
                backgroundColor: tempColor
              }}
            />
          </div>

          {/* Hue Slider */}
          <div
            ref={hueSliderRef}
            className="relative w-full h-6 rounded-lg cursor-pointer"
            style={{
              background: `linear-gradient(to right, ${HUE_COLORS.join(', ')})`
            }}
            onMouseDown={handleHueMouseDown}
            onTouchStart={(e) => handleTouchStart(e, 'hue')}
          >
            {/* Hue Indicator */}
            <div
              className="absolute w-4 h-8 -top-1 border-2 border-white rounded shadow-lg transform -translate-x-1/2 pointer-events-none"
              style={{
                left: `${hsv.h * 100}%`,
                backgroundColor: hueColor
              }}
            />
          </div>

          {/* Preview & Hex */}
          <div className="flex items-center gap-3">
            <div
              className="w-12 h-12 rounded-xl border-2 border-surface-200 flex-shrink-0"
              style={{ backgroundColor: tempColor }}
            />
            <div className="flex-1">
              <span className="text-xs text-surface-500">Selected Color</span>
              <p className="font-mono text-lg uppercase text-surface-900">{tempColor}</p>
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2.5 border border-surface-200 rounded-xl text-surface-700 font-medium hover:bg-surface-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleApply}
              className="flex-1 px-4 py-2.5 bg-primary-500 text-white rounded-xl font-medium hover:bg-primary-600 transition-colors"
            >
              Apply
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function ColorPicker({ value, onChange }) {
  const [isOpen, setIsOpen] = useState(false);
  const [isCustomOpen, setIsCustomOpen] = useState(false);
  const isOpenRef = useRef(false);
  const isCustomOpenRef = useRef(false);
  const closeModal = useCallback(() => setIsOpen(false), []);

  useModalBack(isOpen, closeModal);

  // Keep refs in sync
  useEffect(() => { isOpenRef.current = isOpen; }, [isOpen]);
  useEffect(() => { isCustomOpenRef.current = isCustomOpen; }, [isCustomOpen]);

  // Close modals on browser back button
  useEffect(() => {
    const handlePopState = () => {
      if (isCustomOpenRef.current) {
        setIsCustomOpen(false);
      } else if (isOpenRef.current) {
        setIsOpen(false);
      }
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  const openPresetModal = useCallback(() => {
    setIsOpen(true);
    window.history.pushState({ colorPresetModal: true }, '');
  }, []);

  const closePresetModal = useCallback(() => {
    if (isOpenRef.current) {
      setIsOpen(false);
      window.history.back();
    }
  }, []);

  const handleColorSelect = useCallback((color) => {
    onChange(color);
    if (isOpenRef.current) {
      setIsOpen(false);
      window.history.back();
    }
  }, [onChange]);

  const handleCustomColorClick = useCallback(() => {
    setIsCustomOpen(true);
    window.history.pushState({ colorCustomModal: true }, '');
  }, []);

  const closeCustomModal = useCallback(() => {
    if (isCustomOpenRef.current) {
      setIsCustomOpen(false);
      window.history.back();
    }
  }, []);

  const handleCustomColorChange = useCallback((color) => {
    onChange(color);
    // Close both modals: custom first, then preset
    if (isCustomOpenRef.current) {
      setIsCustomOpen(false);
      window.history.back();
    }
    if (isOpenRef.current) {
      setIsOpen(false);
      window.history.back();
    }
  }, [onChange]);

  return (
    <div className="relative">
      <button
        type="button"
        onClick={openPresetModal}
        className="w-full flex items-center gap-3 px-4 py-3 border border-surface-200 rounded-xl hover:border-surface-300 transition-colors bg-white"
      >
        <div
          className="w-9 h-9 rounded-lg border-2 border-white shadow-sm flex-shrink-0"
          style={{ backgroundColor: value || '#6366f1' }}
        />
        <span className="flex-1 text-left text-sm text-surface-600 font-medium">
          Color
        </span>
        <ChevronDown className={`w-5 h-5 text-surface-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {/* Preset Colors Modal */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-surface-900/60 backdrop-blur-sm flex items-center justify-center z-[100] p-4"
          onClick={closePresetModal}
        >
          <div
            className="bg-white rounded-2xl shadow-2xl w-full max-w-sm max-h-[80vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-surface-200">
              <h3 className="text-lg font-semibold text-surface-900">Select Color</h3>
              <button
                type="button"
                onClick={closePresetModal}
                className="p-2 text-surface-400 hover:text-surface-600 rounded-lg transition-colors hover:bg-surface-100"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Colors Grid - Scrollable */}
            <div className="flex-1 overflow-y-auto p-4">
              <div className="grid grid-cols-6 gap-2">
                {PRESET_COLORS.map((color) => (
                  <button
                    key={color}
                    type="button"
                    onClick={() => handleColorSelect(color)}
                    className={`aspect-square rounded-xl transition-all flex items-center justify-center ${
                      value === color ? 'ring-2 ring-offset-2 ring-surface-900 scale-110' : 'hover:scale-105'
                    }`}
                    style={{ backgroundColor: color }}
                    title={color}
                  >
                    {value === color && (
                      <Check className="w-5 h-5 text-white drop-shadow-md" />
                    )}
                  </button>
                ))}
              </div>

              {/* Custom Color Button */}
              <div className="mt-4 pt-4 border-t border-surface-200">
                <button
                  type="button"
                  onClick={handleCustomColorClick}
                  className="w-full flex items-center justify-center gap-3 px-4 py-3 bg-surface-100 hover:bg-surface-200 rounded-xl transition-colors"
                >
                  <Palette className="w-5 h-5 text-surface-600" />
                  <span className="text-surface-700 font-medium">Pick Custom Color</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Custom Color Picker Modal */}
      <CustomColorPickerModal
        isOpen={isCustomOpen}
        onClose={closeCustomModal}
        value={value || '#6366f1'}
        onChange={handleCustomColorChange}
      />
    </div>
  );
}
